import { useTemplateHelper } from '../../hooks/useTemplateHelper';
import useWorkOrderLuggage from '../../hooks/useWorkOrderLuggage';
import { useAppSelector } from '../../redux/Store';
import { useEffect, useState } from 'react';
import { Drawer, Toolbar, Box, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useLocation } from 'react-router';
import CustomTypography from '../../ui/atoms/CustomTypography';
import { useFindPermissions } from '../../utils/helpers/useFindPermissions';

const DRAWER_WIDTH = 280;
const COLLAPSED_WIDTH = 70; // Fully collapsed width

const Sidebar = () => {
  const [open, setOpen] = useState(true);
  const location = useLocation();

  return (
    <>
      <Drawer
        variant='permanent'
        open={open}
        sx={{
          width: open ? `${DRAWER_WIDTH}px` : `${COLLAPSED_WIDTH}px`,
          flexShrink: 0,
          transition: 'width 0.3s ease',
          [`& .MuiDrawer-paper`]: {
            width: open ? `${DRAWER_WIDTH}px` : `${COLLAPSED_WIDTH}px`,
            top: '10px',
            bottom: 0,
            boxSizing: 'border-box',
            backgroundImage: 'url(/images/sidebar-bg.jpg)',
            backgroundPosition: 'top',
            backgroundRepeat: 'repeat-x',
            backgroundSize: '1% 100%',
            transition: 'width 0.3s ease',
            overflowX: 'hidden',
          },
        }}
      >
        <Toolbar />
        {location.pathname !== '/home' && (
          <Box
            className='sidebar'
            sx={{
              color: 'white',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              position: 'relative',
            }}
          >
            {open && (
              <Box className='mb-4' sx={{ paddingX: '10px' }}>
                <OrderInformationView />
              </Box>
            )}
          </Box>
        )}
      </Drawer>

      <IconButton
        onClick={() => setOpen(!open)}
        sx={{
          position: 'fixed',
          top: '70px',
          left: open ? `${DRAWER_WIDTH - 20}px` : `${COLLAPSED_WIDTH - 20}px`,
          backgroundColor: 'black',
          borderRadius: '50%',
          zIndex: 1300,
          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.8)' },
        }}
      >
        {open ? (
          <ChevronLeft sx={{ color: 'white' }} />
        ) : (
          <ChevronRight sx={{ color: 'white' }} />
        )}
      </IconButton>
    </>
  );
};

const OrderInformationView = ({ details }: any) => {
  const { getWorkOrderLuggage } = useWorkOrderLuggage();
  const { SideBarTemplate, memoizedLuggage, applyTemplate } =
    useTemplateHelper();

  const order = useAppSelector((state) => state.inspactOrder.currentOrder);
  const [mount, setMount] = useState<boolean>(false);
  const { canViewWorkOrderLiteLuggage } = useFindPermissions();

  useEffect(() => {
    if (!order?.orderNumber || !canViewWorkOrderLiteLuggage) return;

    getWorkOrderLuggage({
      loanId: order?.propertyLoanId,
      orderNumber: order?.orderNumber,
    });
  }, [canViewWorkOrderLiteLuggage, getWorkOrderLuggage, order]);

  useEffect(() => {
    if (Object.keys(memoizedLuggage).length === 0) {
      return setMount(false);
    }
    setMount(true);
  }, [memoizedLuggage]);

  if (!mount) return null;

  return (
    <Box>
      {/* ✅ Rendering Headings */}
      {SideBarTemplate.headings.map((heading: any, index) => (
        <HeadingsComponent
          key={index}
          index={index}
          {...heading}
          details={details}
        />
      ))}

      {/* ✅ Rendering Groups */}
      {SideBarTemplate.groups.map((group, index) => (
        <GroupComponent
          key={index}
          index={index}
          {...group}
          details={details}
        />
      ))}
    </Box>
  );
};

/**
 * ✅ Group Component for rendering Sidebar sections
 */
const GroupComponent = ({ caption, captionRef, fields }: any) => {
  const { resolveReference } = useTemplateHelper();
  const title = caption || resolveReference(captionRef);

  return (
    <ul style={{ listStyleType: 'none', padding: 0, margin: '0.5rem 0' }}>
      <li
        style={{
          fontWeight: 'bold',
          fontSize: '0.90rem',
          fontFamily: 'mont-regular, sans-serif',
          textTransform: 'uppercase',
          margin: '0.5rem 0',
        }}
      >
        {title}
      </li>
      {fields.map((f: any, index: number) => (
        <RenderFieldData key={index} index={index} {...f} />
      ))}
    </ul>
  );
};

/**
 * ✅ Render Field Data with Handlebars Precompiled Templates
 */
const RenderFieldData = ({
  caption,
  captionRef,
  valueRef,
  templateKey,
  details,
  index,
  ...otherProps
}: any) => {
  const { defaultTemplateKey, applyTemplate, generateModel, resolveReference } =
    useTemplateHelper();

  const activeTemplateKey = templateKey || defaultTemplateKey;
  const title =
    caption || applyTemplate(defaultTemplateKey, resolveReference(captionRef));
  const model = valueRef
    ? resolveReference(valueRef)
    : generateModel(otherProps);

  const isBlock = title.startsWith('_');

  return (
    <ul key={title} style={{ listStyleType: 'none', padding: 0, margin: '0' }}>
      <li
        style={{
          display: isBlock ? 'block' : 'grid',
          gridTemplateColumns: '50% 50%',
          alignItems: 'start',
          fontFamily: 'mont-regular, sans-serif',
          padding: '8px 10px',
          fontSize: '0.90rem',
          borderRadius: '4px',
          backgroundColor:
            index % 2 === 0 ? 'rgba(255,255,255,0.1)' : 'transparent',
          wordBreak: 'break-word',
          width: '100%',
        }}
      >
        {isBlock ? (
          <div style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>
            {title.slice(1)}
          </div>
        ) : (
          <span style={{ fontWeight: 'normal', fontSize: '0.85rem' }}>
            {title}
          </span>
        )}
        <span
          style={{
            textAlign: 'left',
            fontSize: '0.85rem',
            wordWrap: 'break-word',
          }}
          dangerouslySetInnerHTML={{
            __html: applyTemplate(activeTemplateKey, model),
          }}
        />
      </li>
    </ul>
  );
};

/**
 * ✅ Heading Component for rendering Sidebar headings
 */
const HeadingsComponent = ({
  caption,
  captionRef,
  valueRef,
  templateKey,
  details,
  index,
  ...otherProps
}: any) => {
  const { defaultTemplateKey, applyTemplate, resolveReference, generateModel } =
    useTemplateHelper();

  const activeTemplateKey = templateKey || defaultTemplateKey;
  const title =
    caption || applyTemplate(defaultTemplateKey, resolveReference(captionRef));
  const displayValue = valueRef
    ? applyTemplate(activeTemplateKey, resolveReference(valueRef))
    : applyTemplate(activeTemplateKey, generateModel(otherProps));

  return (
    <ul style={{ listStyleType: 'none', padding: 0, margin: 0 }}>
      <li
        style={{
          padding: '5px',
          borderRadius: '4px',
          fontSize: '0.80rem',
          wordBreak: 'break-word',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <CustomTypography
          variant='h5'
          sx={{
            fontWeight: 'normal',
            fontSize: '1rem',
            textTransform: 'uppercase',
            minWidth: '100px',
          }}
        >
          {title}
        </CustomTypography>
        {displayValue && (
          <CustomTypography
            variant='h5'
            sx={{
              fontWeight: 'normal',
              fontSize: '1rem',
              textTransform: 'uppercase',
            }}
          >
            {displayValue}
          </CustomTypography>
        )}
      </li>
    </ul>
  );
};

export default Sidebar;
